import { getRequestedQuantityForProduct } from '@/features/shared/utils/normalizeProduct'
import { parseCurrency, formatPrice } from '@/features/shared/utils/formatter'
import {
  type AviatorOrder,
  type OrderTrackingResponse,
  type Order,
  type OrderDisplayValue,
  type OrderStatus,
} from '@/features/shop/services/Orders/types'
import { type TransformedOrderTracking } from '@/features/shop/services/Product/types'
import { isToday } from 'date-fns/isToday'
import { isOutForDeliveryOrderStatus, isPastOrderStatus } from '@/features/shared/utils/orders'
import { type MutationProduct } from '@shared/types'

export const getOrderDisplayValuesAsInt = (
  displayValues?: OrderDisplayValue[] | OrderDisplayValue | null,
): Record<string, number> => {
  if (!displayValues) return {}
  const displayValuesList = Array.isArray(displayValues)
    ? displayValues
    : displayValues.display_values
  if (!displayValuesList) return {}
  return displayValuesList.reduce<Record<string, number>>((accum, displayValue) => {
    if (displayValue.key) {
      accum[displayValue.key] = parseCurrency(displayValue.value)
    }
    if (displayValue.display_values) {
      const nested = getOrderDisplayValuesAsInt(displayValue)
      return {
        ...accum,
        ...nested,
      }
    } else {
      return accum
    }
  }, {})
}

export function getFormattedOrderDeliveryFee(order: Order) {
  const numericDeliveryFee = getOrderDisplayValuesAsInt(order.display_values)['delivery_fee'] ?? 0
  let formattedDeliveryFee = formatPrice(numericDeliveryFee)

  const emptySuffix = '.00'

  if (formattedDeliveryFee.endsWith(emptySuffix)) {
    formattedDeliveryFee = formattedDeliveryFee.substring(
      0,
      formattedDeliveryFee.length - emptySuffix.length,
    )
  }
  return formattedDeliveryFee
}

export const getOrderLinesForUpdateOrder = (
  order: Order,
  products: Record<string | number, MutationProduct>,
) =>
  order.order_lines
    .map((line) => {
      const { requested_product } = line
      const { id, name, isCustomProduct } = requested_product
      const changedProductInOrder = products[isCustomProduct ? name : id]
      if (changedProductInOrder) {
        return {
          ...line,
          requested_qty: getRequestedQuantityForProduct(changedProductInOrder),
          requested_product: {
            ...requested_product,
            ...changedProductInOrder,
          },
          notes: changedProductInOrder.note || '',
        }
      }
      return line
    })
    .filter((line) => line.requested_qty > 0)

export const transformOrderTrackingProduct = (
  orderTrackingResponse: OrderTrackingResponse,
  status: Order['status'],
): TransformedOrderTracking => {
  const foundProducts = []
  const notFoundProducts = []
  const stillShoppingProducts = []
  const substitutedProducts = []
  const pendingProducts = []

  for (const product of orderTrackingResponse.products) {
    if (product.shop_status === 'found' && product.fulfilled) {
      foundProducts.push(product.fulfilled)
      continue
    }

    if (product.shop_status === 'not_found') {
      notFoundProducts.push(product.requested)
      continue
    }

    if (product.shop_status === 'not_shopped') {
      stillShoppingProducts.push(product.requested)
      continue
    }

    if (product.shop_status === 'substituted' && product.fulfilled) {
      const { fulfilled, requested } = product

      const wasSuccessfullyFulfilled =
        //* If out for delivery, then any potentially pending subs go to substituted state
        isOutForDeliveryOrderStatus(status) ||
        //* A substitution should sort to pending if there is a member_approval_status and the status is 'pending', otherwise an action is either not needed or has
        //* already been taken on this substitution, and should not go to pending state
        !product.member_approval_status ||
        product.member_approval_status === 'accepted'

      if (wasSuccessfullyFulfilled) {
        substitutedProducts.push({ fulfilled, requested })
        continue
      }

      //* We have a substitution and the pending status indicates we are waiting on member to take action (approve or decline)
      if (product.member_approval_status === 'pending') {
        pendingProducts.push({
          fulfilled,
          requested,
        })
        continue
      }

      //* If we get here, then the approval status is declined, so the item must move back to still shopping state
      stillShoppingProducts.push(product.requested)
    }
  }

  return {
    foundProducts,
    notFoundProducts,
    stillShoppingProducts,
    substitutedProducts,
    pendingProducts,
  }
}

export function findEarliestActiveOrderToday(orders: AviatorOrder[]): AviatorOrder | null {
  const todaysActiveOrders = orders.filter(isOrderLive)
  return todaysActiveOrders.reduce<AviatorOrder | null>((prev, curr) => {
    if (!prev) return curr
    return new Date(prev.time_end) < new Date(curr.time_end) ? prev : curr
  }, null)
}

export function isOrderLive(order: { time_end: string; status: OrderStatus }): boolean {
  return (
    // order is scheduled for today and order is not in completed state
    isToday(new Date(order.time_end)) && !isPastOrderStatus(order.status)
  )
}
